<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { Building2Icon, ChevronsUpDownIcon, LogOutIcon, UserIcon } from '@lucide/vue';
import LogoutForm from '@/views/auth/Partials/logout-form.vue';
import UserAvatar from '@/components/user-avatar.vue';

const props = defineProps({
  isBigScreen: Boolean,
});

const store = useStore();

const user = computed(() => store.state.me.user);

// --- Popup positioning ---

const reference = ref(null);
const floating = ref(null);
const menuPlacement = computed(() => (props.isBigScreen ? 'right-end' : 'top-start'));

const { floatingStyles } = useFloating(reference, floating, {
  placement: menuPlacement,
  middleware: [offset(4), flip(), shift()],
  whileElementsMounted: autoUpdate,
});

const itemClass = 'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-neutral-700';
const itemActiveClass = 'bg-neutral-100 text-neutral-900';
</script>

<template>
  <Menu
    as="div"
    class="relative"
    v-slot="{ open }">
    <MenuButton
      id="navAccount"
      ref="reference"
      :class="[
        'flex h-12 w-full items-center gap-2 overflow-hidden rounded-md text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        open
          ? 'bg-neutral-100 text-neutral-600 md:bg-transparent md:text-white'
          : 'text-white hover:bg-neutral-100 hover:text-neutral-600 md:hover:bg-transparent md:hover:text-white',
      ]">
      <UserAvatar
        :name="user.name"
        :email="user.email"
        :size="32"
        class="shrink-0" />
      <span class="truncate font-semibold">{{ user.name }}</span>
      <ChevronsUpDownIcon class="ml-auto size-4 shrink-0" />
    </MenuButton>

    <transition
      enter-active-class="transition-opacity ease-out duration-100"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity ease-in duration-75"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <MenuItems
        v-if="open"
        static
        ref="floating"
        :style="floatingStyles"
        class="z-50 w-56 rounded-lg border border-neutral-200 bg-white p-1 shadow-md focus:outline-none">
        <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm text-neutral-700">
          <UserAvatar
            :name="user.name"
            :email="user.email"
            :size="32"
            class="shrink-0" />
          <div class="grid flex-1 leading-tight">
            <span class="truncate font-semibold">{{ user.name }}</span>
            <span class="truncate text-xs text-neutral-500">{{ user.email }}</span>
          </div>
        </div>

        <div class="my-1 h-px bg-neutral-200"></div>

        <MenuItem v-slot="{ active }">
          <RouterLink
            id="navTeams"
            :to="{ name: 'teams.index' }"
            :class="[itemClass, active ? itemActiveClass : '']">
            <Building2Icon class="size-4 shrink-0" />
            Teams
          </RouterLink>
        </MenuItem>

        <MenuItem v-slot="{ active }">
          <RouterLink
            :to="{ name: 'account.show' }"
            :class="[itemClass, active ? itemActiveClass : '']">
            <UserIcon class="size-4 shrink-0" />
            Profile
          </RouterLink>
        </MenuItem>

        <div class="my-1 h-px bg-neutral-200"></div>

        <MenuItem v-slot="{ active }">
          <LogoutForm
            button-id="navLogout"
            :button-class="`${itemClass} ${active ? itemActiveClass : ''}`">
            <LogOutIcon class="size-4 shrink-0" />
            Log Out
          </LogoutForm>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>
</template>
